// Libs
import { Box, IconButton, Typography } from '@mui/material';
import classNames from 'classnames/bind';
import moment from 'moment';
import { useState } from 'react';
// Components, Layouts, Pages
// Others
import { CommonIconArrowSvg } from '~/assets/svgComponents';
import { varColor } from '~/themes/themeVariables';
import { DEFAULT_MIN_YEAR } from '~/utils/constants/common';
import { DateFormatEnum } from '~/utils/enum/common';
import { useOutsideClick } from '~/utils/hooks/useOutsideClick';
// Styles, images, icons
import styles from './HeaderDatePicker.module.scss';

type Props = {
  date: Date;
  onChangeYear: (year: number) => void;
  onChangeMonth: (month: number) => void;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
};

const cx = classNames.bind(styles);

const HeaderDatePicker = (props: Props) => {
  //#region Destructuring Props
  const {
    date,
    onChangeYear,
    onChangeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isOpenOptionMonth, setIsOpenOptionMonth] = useState<boolean>(false);
  const [isOpenOptionYear, setIsOpenOptionYear] = useState<boolean>(false);
  const monthRef = useOutsideClick<HTMLDivElement>(() => setIsOpenOptionMonth(false));
  const yearRef = useOutsideClick<HTMLDivElement>(() => setIsOpenOptionYear(false));
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleChangeMonth = (month: number) => {
    onChangeMonth(month);
    setIsOpenOptionMonth(!isOpenOptionMonth);
  };

  const handleChangeYear = (year: number) => {
    onChangeYear(year);
    setIsOpenOptionYear(!isOpenOptionYear);
  };

  const handleOpenOptionMonth = () => {
    setIsOpenOptionMonth(!isOpenOptionMonth);
  };

  const handleOpenOptionYear = () => {
    setIsOpenOptionYear(!isOpenOptionYear);
  };

  const getYear = () => {
    const currentYear = new Date().getFullYear();

    const years = [];

    for (let year = DEFAULT_MIN_YEAR; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };
  //#endregion Handle Function

  return (
    <Box id='headerDatePickerComponent' className={cx('container')}>
      <Box className={cx('header')}>
        <IconButton
          color='primary'
          onClick={decreaseMonth}
          disabled={prevMonthButtonDisabled}
          className={cx('iconButton')}
        >
          <Box className={cx('iconArrowLeft')}>
            <CommonIconArrowSvg strokePath={varColor.textPrimary} width={20} height={20} />
          </Box>
        </IconButton>

        <Typography className={cx('titleHeader')}>{moment(date).format(DateFormatEnum.MMMM_YYYY)}</Typography>

        <IconButton
          color='primary'
          onClick={increaseMonth}
          disabled={nextMonthButtonDisabled}
          className={cx('iconButton')}
        >
          <Box className={cx('iconArrowRight')}>
            <CommonIconArrowSvg strokePath={varColor.textPrimary} width={20} height={20} />
          </Box>
        </IconButton>
      </Box>

      <Box className={cx('customDateSelect')}>
        <Box ref={monthRef} className={cx('customMonthSelector')}>
          <Box className={cx('selectBtn')} onClick={handleOpenOptionMonth}>
            <Typography className={cx('value')}>{moment(date).format('MMMM')}</Typography>

            <CommonIconArrowSvg strokePath={varColor.textPrimary} />
          </Box>

          {isOpenOptionMonth && (
            <Box component={'ul'} className={cx('options')}>
              <Box className={cx('listOption')}>
                {moment.months().map((month, index) => (
                  <Box
                    component={'li'}
                    key={month}
                    className={cx('option', index === moment(date).month() && 'active')}
                  >
                    <Typography className={cx('changeMonthBtn')} onClick={() => handleChangeMonth(index)}>
                      {month}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box ref={yearRef} className={cx('customYearSelector')}>
          <Box className={cx('selectBtn')} onClick={handleOpenOptionYear}>
            <Typography className={cx('value')}>{moment(date).format('YYYY')}</Typography>
            <CommonIconArrowSvg strokePath={varColor.textPrimary} />
          </Box>

          {isOpenOptionYear && (
            <Box component={'ul'} className={cx('options')}>
              <Box className={cx('listOption')}>
                {getYear().map((year) => (
                  <Box
                    component={'li'}
                    key={year}
                    className={cx('option', year === moment(date).year() && 'active')}
                  >
                    <Typography className={cx('changeYearBtn')} onClick={() => handleChangeYear(year)}>
                      {year}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default HeaderDatePicker;
